<template>
    <div v-if="!loading">
        <div class="header-actions">
            <CorpId />
            <el-dropdown class="flex-level" @command="handleConfigForm">
                <CompassButton
                    :label="$t.__('developerPage.Webhooks.create_webhook')"
                    type="primary"
                    dropdown
                    class="flex-end"
                />
                <template #dropdown>
                    <el-dropdown-menu class="flex-end">
                        <el-dropdown-item
                            v-for="hook in availablesWebhooks"
                            :key="hook.type"
                            :command="hook"
                        >
                            Webhook {{ hook.type }}
                        </el-dropdown-item>
                    </el-dropdown-menu>
                </template>
            </el-dropdown>
        </div>

        <!-- DESCRIPTOON -->
        <p class="mt-0">
            {{
                $l10n.__(
                    "Les webhooks vous permettent de recevoir en temps réel les notifications d'activité de votre compte Sellsy dans une application tierce.",
                )
            }}
        </p>
        <p>{{ $l10n.__('Ils permettent par exemple') }}:</p>
        <ul>
            <li>
                {{
                    $l10n.__(
                        '%(IlsPermettentParExemple)sDe recevoir les notifications textuelles de Sellsy dans le Slack de votre entreprise.',
                        { IlsPermettentParExemple: '' },
                    )
                }}
            </li>
            <li>
                {{
                    $l10n.__(
                        "%(IlsPermettentParExemple)sD'envoyer les notifications au format JSON vers un autre système d'information et ainsi de savoir si l'application tierce a besoin d'appeler l'API de Sellsy pour effectuer une mise à jour.",
                        { IlsPermettentParExemple: '' },
                    )
                }}
            </li>
        </ul>

        <!-- READ MORE -->
        <p>
            <a
                href="https://help.sellsy.com/hc/gestion-des-synchronisations/webhooks"
                target="_blank"
            >
                {{
                    $l10n.__(
                        'Pour en savoir plus sur le fonctionnement des webhooks, vous pouvez consulter notre article de FAQ.',
                    )
                }}
            </a>
            <br />
            <br />
            <span id="faq-webhook-ip-address"></span>
        </p>

        <!-- WARNING MESSAGE -->
        <div class="alert alert-danger my-3">
            <p class="mt-0">
                {{
                    $l10n.__(
                        'Attention, les notifications envoyées via les Webhooks sont définies au niveau de votre compte et ne tiennent pas compte des privilèges de vos collaborateurs.',
                    )
                }}
            </p>
            <p class="mb-0">
                {{
                    $l10n.__(
                        "Si vous décidez par exemple d'envoyer une notification Slack à chaque nouveau paiement, tous les collaborateurs présents sur le channel pourront la voir.",
                    )
                }}
            </p>
        </div>

        <!-- PAGINATION -->
        <div class="flex-row mt-2">
            <paginator
                class="flex-item-right"
                :pagination="paginator"
                @pagination:update="goToPage"
            />
        </div>

        <!-- TABLE -->
        <el-table
            v-loading="loadingTable"
            :data="webhooks"
            border
            :emptyText="$t.__('developerPage.Webhooks.no_webhook_configured')"
            :defaultSort="{ prop: list[0].prop, order: 'ascending' }"
            class="fixed-row-height wp-100 mt-2"
        >
            <!-- COLUMNS -->
            <template v-for="(column, index) in list" :key="index">
                <el-table-column
                    v-if="column.prop !== 'status'"
                    :key="index"
                    :prop="column.prop"
                    :label="column.label"
                    showOverflowTooltip
                    sortable
                    :width="[index > 0 ? 150 : '']"
                >
                </el-table-column>

                <!-- STATUS -->
                <el-table-column v-else :label="column.label" width="150">
                    <template #default="scope">
                        <template v-if="scope.row.is_enabled">
                            <div class="webhook-status">
                                <CompassTag size="small" color="green">
                                    {{ $l10n.__('Actif') }}
                                </CompassTag>
                            </div>
                        </template>
                        <template v-else-if="scope.row.is_enabled === false">
                            <div class="webhook-status">
                                <CompassTag size="small" color="gray">
                                    {{ $l10n.__('Désactivé') }}
                                </CompassTag>
                            </div>
                        </template>
                        <template v-else>
                            <div class="webhook-status">
                                <CompassTag size="small" color="gray">
                                    {{ $l10n.__('Non configuré') }}
                                </CompassTag>
                            </div>
                        </template>
                    </template>
                </el-table-column>
            </template>

            <!-- ACTIONS -->
            <el-table-column fixed="right" :label="$t.__('common.labels.actions')" width="104">
                <template #default="scope">
                    <div class="flex-row">
                        <CompassTooltip
                            placement="bottom"
                            :content="$t.__('developerPage.Webhooks.configuration_error')"
                            contentClass="tooltip-btn-webhooks"
                            :disabled="!isWebhookNotEditable(scope.row)"
                            :arrow="false"
                        >
                            <CompassIconButton
                                icon="settings"
                                type="tertiary"
                                :disabled="isWebhookNotEditable(scope.row)"
                                @click="handleConfigForm(scope.row)"
                            />
                        </CompassTooltip>
                        <CompassTooltip
                            :content="$t.__('developerPage.Webhooks.delete_webhook_error')"
                            placement="bottom"
                            contentClass="tooltip-btn-webhooks"
                            :disabled="!isWebhookNotEditable(scope.row)"
                            :arrow="false"
                        >
                            <CompassIconButton
                                icon="bin"
                                type="tertiary"
                                :disabled="isWebhookNotEditable(scope.row)"
                                class="ml-2"
                                @click="deleteWebhook(scope.row)"
                            />
                        </CompassTooltip>
                    </div>
                </template>
            </el-table-column>
        </el-table>

        <webhooks-modal
            v-if="openConfigForm"
            class="webhook-modal"
            :visible="openConfigForm"
            :config="configForm"
            @close="handleCloseConfigForm"
        ></webhooks-modal>
    </div>

    <preloader v-else :background="false" :display="true" />
</template>

<script>
import Preloader from 'vcomponents/utilities/Preloader.vue';
import WebhooksModal from 'vcomponents/developerPage/WebhooksForms/WebhooksModal.vue';
import WebhookApi from 'api/webhooks';
import Paginator from 'vcomponents/utilities/Paginator.vue';
import CorpId from 'vcomponents/developerPage/CorpId.vue';

export default {
    components: {
        Preloader,
        WebhooksModal,
        Paginator,
        CorpId,
    },
    data() {
        return {
            list: [
                { prop: 'endpoint', label: this.$l10n.__('Endpoint') },
                { prop: 'name', label: this.$l10n.__('Nom') },
                { prop: 'type', label: this.$l10n.__('Type') },
                { prop: 'status', label: this.$l10n.__('État') },
            ],
            loading: false,
            loadingTable: false,
            webhooks: [],
            availablesWebhooks: [{ type: 'slack' }, { type: 'http' }],
            openConfigForm: false,
            configForm: {},
            paginator: {
                current_page: 1,
                last_page: 1,
                total: 0,
            },
            corpId: null,
        };
    },
    created() {
        this.getList();
        this.corpId = this.$store.getters['corp/corpId'];
        EventBus.on('webhooks:refresh', this.getList);
    },
    unmounted() {
        EventBus.off('webhooks:refresh', this.getList);
    },
    methods: {
        getList() {
            this.loading = true;
            WebhookApi.getWebhooks()
                .then((response) => {
                    this.loading = false;
                    this.webhooks = response.data.data;
                    this.paginator = {
                        ...this.paginator,
                        ...response.data.pagination,
                        last_page: Math.ceil(
                            response.data.pagination.total / response.data.pagination.limit,
                        ),
                        total: response.data.pagination.total,
                    };
                })
                .catch(() => {
                    this.loading = false;
                });
        },
        handleConfigForm(event) {
            this.configForm = event;
            this.openConfigForm = true;
        },
        handleCloseConfigForm() {
            this.configForm = {};
            this.openConfigForm = false;
        },
        goToPage(event) {
            this.loadingTable = true;
            const offset = (event - 1) * this.paginator.limit;
            WebhookApi.getWebhooks({ offset })
                .then((response) => {
                    this.webhooks = response.data.data;
                    this.paginator.current_page = event;
                    this.loadingTable = false;
                })
                .catch(() => {
                    this.loadingTable = false;
                });
        },
        deleteWebhook(row) {
            WebhookApi.deleteWebhook(row.id).then(() => {
                this.getList();
            });
        },
        isWebhookNotEditable(column) {
            //We have to avoid remove or edit this webhook from rocketChart
            return (
                column.endpoint ===
                'https://events.hookdeck.com/e/src_dk8GXi05kRbE/sellsy/invoiceUpdated'
            );
        },
    },
};
</script>
<style lang="scss">
#sellsy_app #contentDiv .webhook-modal .el-dialog {
    max-width: 1100px;
    width: 100%;
}

.tooltip-btn-webhooks {
    max-width: 250px;
    white-space: pre-line;
}
</style>
<style lang="scss" scoped>
.header-actions {
    align-items: center;
    display: flex;
    position: absolute;
    right: 0;
    top: 12px;
    .el-dropdown {
        margin: 0 0 0 16px;
    }
}

.webhook-status {
    align-items: center;
    display: flex;
    height: 100%;
}
</style>
